
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Pencil, Trash2, Plus, Check, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useBillingPlans } from '@/hooks/useBillingPlans';
import { BillingPlan } from '@/types/billing';

const defaultPlanData = {
  id: '',
  name: '',
  price: 0,
  user_limit: 1,
  is_enterprise: false,
  is_active: true,
  features: {}
};

const PlanManagement = () => {
  const { billingPlans, loading, refetch } = useBillingPlans();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<BillingPlan | null>(null);
  const [planData, setPlanData] = useState(defaultPlanData);
  const [features, setFeatures] = useState<string[]>([]);
  const [newFeature, setNewFeature] = useState('');

  const handleEditPlan = (plan: BillingPlan) => {
    setSelectedPlan(plan);
    setPlanData({
      id: plan.id,
      name: plan.name,
      price: plan.price,
      user_limit: plan.user_limit,
      is_enterprise: plan.is_enterprise,
      is_active: plan.is_active,
      features: plan.features || {}
    });
    
    // Convert features object to array
    const featureArray = plan.features 
      ? Object.entries(plan.features).map(([key, value]) => 
          value === true ? key : ''
        ).filter(Boolean)
      : [];
    
    setFeatures(featureArray);
    setIsEditDialogOpen(true);
  };

  const handleDeletePlan = (plan: BillingPlan) => {
    setSelectedPlan(plan);
    setIsDeleteDialogOpen(true);
  };

  const handleCreateNewPlan = () => {
    setSelectedPlan(null);
    setPlanData(defaultPlanData);
    setFeatures([]);
    setIsEditDialogOpen(true);
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFeatures([...features, newFeature.trim()]);
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const savePlan = async () => {
    try {
      // Convert features array to object with boolean values
      const featuresObject: Record<string, boolean> = {};
      features.forEach(feature => {
        featuresObject[feature] = true;
      });

      const formattedPlanData = {
        ...planData,
        features: featuresObject
      };

      let result;
      
      if (selectedPlan) {
        // Update existing plan
        result = await supabase
          .from('billing_plans')
          .update(formattedPlanData)
          .eq('id', selectedPlan.id);
      } else {
        // Create new plan
        const planId = planData.id || planData.name.toLowerCase().replace(/\s+/g, '_');
        
        result = await supabase
          .from('billing_plans')
          .insert({
            ...formattedPlanData,
            id: planId
          });
      }

      if (result.error) throw result.error;
      
      toast({
        title: selectedPlan ? "Forfait mis à jour" : "Forfait créé",
        description: `Le forfait "${planData.name}" a été ${selectedPlan ? 'mis à jour' : 'créé'} avec succès.`
      });
      
      refetch();
      setIsEditDialogOpen(false);
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde du forfait:', error);
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de l'enregistrement du forfait.",
        variant: "destructive"
      });
    }
  };

  const deletePlan = async () => {
    if (!selectedPlan) return;
    
    try {
      const { error } = await supabase
        .from('billing_plans')
        .delete()
        .eq('id', selectedPlan.id);
      
      if (error) throw error;
      
      toast({
        title: "Forfait supprimé",
        description: `Le forfait "${selectedPlan.name}" a été supprimé avec succès.`
      });
      
      refetch();
      setIsDeleteDialogOpen(false);
    } catch (error: any) {
      console.error('Erreur lors de la suppression du forfait:', error);
      toast({
        title: "Erreur",
        description: error.message || "Une erreur est survenue lors de la suppression du forfait.",
        variant: "destructive"
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Gestion des forfaits</CardTitle>
          <CardDescription>
            Créez et gérez les différents forfaits disponibles sur la plateforme
          </CardDescription>
        </div>
        <Button onClick={handleCreateNewPlan} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Nouveau forfait
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Prix</TableHead>
                <TableHead>Limite utilisateurs</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {billingPlans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    Aucun forfait trouvé. Créez-en un pour commencer.
                  </TableCell>
                </TableRow>
              ) : (
                billingPlans.map(plan => (
                  <TableRow key={plan.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center">
                        <CreditCard className="h-4 w-4 mr-2 text-primary" />
                        {plan.name}
                      </div>
                    </TableCell>
                    <TableCell>{plan.price > 0 ? `${plan.price}€` : 'Gratuit'}</TableCell>
                    <TableCell>{plan.user_limit}</TableCell>
                    <TableCell>
                      {plan.is_enterprise ? (
                        <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300">
                          Enterprise
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
                          Standard
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {plan.is_active ? (
                        <Badge className="bg-green-100 text-green-800 border-green-300">
                          Actif
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-300">
                          Inactif
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditPlan(plan)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDeletePlan(plan)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}

        {/* Edit/Create Plan Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle>
                {selectedPlan ? `Modifier le forfait ${selectedPlan.name}` : 'Créer un nouveau forfait'}
              </DialogTitle>
              <DialogDescription>
                {selectedPlan 
                  ? 'Modifiez les détails du forfait ci-dessous.' 
                  : 'Définissez les caractéristiques du nouveau forfait.'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="plan-name">Nom du forfait</Label>
                  <Input
                    id="plan-name"
                    value={planData.name}
                    onChange={(e) => setPlanData({...planData, name: e.target.value})}
                    placeholder="Ex: Pro, Enterprise, etc."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="plan-id">ID (identifiant technique)</Label>
                  <Input
                    id="plan-id"
                    value={planData.id}
                    onChange={(e) => setPlanData({...planData, id: e.target.value})}
                    placeholder="Ex: pro, enterprise, etc."
                    disabled={!!selectedPlan}
                  />
                  {!selectedPlan && (
                    <p className="text-xs text-gray-500">
                      Laissez vide pour générer automatiquement à partir du nom
                    </p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="plan-price">Prix (€)</Label>
                  <Input
                    id="plan-price"
                    type="number"
                    value={planData.price.toString()}
                    onChange={(e) => setPlanData({...planData, price: parseFloat(e.target.value) || 0})}
                    placeholder="0 = Gratuit"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="plan-user-limit">Limite d'utilisateurs</Label>
                  <Input
                    id="plan-user-limit"
                    type="number"
                    value={planData.user_limit.toString()}
                    onChange={(e) => setPlanData({...planData, user_limit: parseInt(e.target.value) || 1})}
                    placeholder="Nombre d'utilisateurs autorisés"
                    min="1"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is-enterprise"
                    checked={planData.is_enterprise}
                    onCheckedChange={(checked) => 
                      setPlanData({...planData, is_enterprise: checked === true})
                    }
                  />
                  <Label htmlFor="is-enterprise">Forfait entreprise</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is-active"
                    checked={planData.is_active}
                    onCheckedChange={(checked) => 
                      setPlanData({...planData, is_active: checked === true})
                    }
                  />
                  <Label htmlFor="is-active">Actif</Label>
                </div>
              </div>
              
              <div className="space-y-2 pt-4">
                <Label>Fonctionnalités incluses</Label>
                <div className="flex space-x-2">
                  <Input
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    placeholder="Nouvelle fonctionnalité"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  />
                  <Button type="button" onClick={addFeature} variant="outline">
                    Ajouter
                  </Button>
                </div>
                
                <div className="mt-2 space-y-2">
                  {features.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">Aucune fonctionnalité définie</p>
                  ) : (
                    features.map((feature, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <div className="flex items-center">
                          <Check className="h-4 w-4 text-green-500 mr-2" />
                          <span>{feature}</span>
                        </div>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => removeFeature(index)}
                          className="h-6 w-6 p-0 text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={savePlan}>
                {selectedPlan ? 'Mettre à jour' : 'Créer le forfait'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmer la suppression</DialogTitle>
              <DialogDescription>
                Êtes-vous sûr de vouloir supprimer le forfait "{selectedPlan?.name}" ?
                Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:justify-end">
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                Annuler
              </Button>
              <Button variant="destructive" onClick={deletePlan}>
                Supprimer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default PlanManagement;
